import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Link,
  IconButton,
  List,
  ListItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import { useLocalization } from '@/hooks/useLocalization';
import { AccountBase } from '@/models/AccountViewModel';
import { useUploadAccountHistoryMutation } from '@/store/accountsApi';

interface UploadAccountHistoryModalProps {
  open: boolean;
  onClose: () => void;
  account: AccountBase;
}

const UploadAccountHistoryModal: React.FC<UploadAccountHistoryModalProps> = ({
  open,
  onClose,
  account,
}) => {
  const { t } = useLocalization();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadAccountHistory, { isLoading: isUploading }] =
    useUploadAccountHistoryMutation();
  const [uploadSuccess, setUploadSuccess] = React.useState<boolean>(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setUploadSuccess(false);
      setUploadError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  const handleSelectFileClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const files = event.target.files;
    const file = files && files.length > 0 ? files[0] : null;
    setSelectedFile(file);
  };

  const handleRemoveSelectedFile = (): void => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      // Reset the input so the same file can be re-selected
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile) return;
    try {
      setUploadError(null);
      await uploadAccountHistory({
        accountId: account.AccountId,
        file: selectedFile,
      }).unwrap();
      setUploadSuccess(true);
    } catch (error) {
      console.error('Failed to upload account history', error);
      setUploadError('error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant='h5'
          sx={{ flex: 1, fontSize: '1.25rem' }}
        >
          {t('uploadAccountHistory.title')}
        </Typography>
        <IconButton
          aria-label={t('close')}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '40px !important' }}>
        {uploadSuccess ? (
          <Alert
            severity='success'
            sx={{ mb: 2 }}
          >
            {t('uploadAccountHistory.uploadSuccess')}
          </Alert>
        ) : (
          <>
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ mb: 2, fontSize: '14px', lineHeight: 1.5 }}
            >
              {t('uploadAccountHistory.intro')}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 3, fontSize: '14px', lineHeight: 1.5 }}
            >
              {t('uploadAccountHistory.note')}
            </Typography>

            <Typography
              variant='subtitle1'
              sx={{ mb: 2, fontWeight: 600 }}
            >
              {t('uploadAccountHistory.acceptedFormats')}
            </Typography>

            <Typography
              variant='body1'
              sx={{ mb: 2 }}
            >
              {t('uploadAccountHistory.structureIntro')}
            </Typography>

            <List
              component='ol'
              sx={{ pl: 3, py: 0, m: 0, mb: 3, listStyleType: 'decimal' }}
            >
              <ListItem
                component='li'
                sx={{ display: 'list-item', p: 0, mb: 2 }}
              >
                <Box>
                  <Typography variant='body1'>
                    {t('uploadAccountHistory.singleDateFormat')} (
                    <Link
                      href={`${import.meta.env.BASE_URL}templates/upload_sample_single_date.xlsx`}
                      underline='hover'
                      download
                    >
                      {t('uploadAccountHistory.downloadTemplate')}
                    </Link>
                    )
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('uploadAccountHistory.singleDateHint')}
                  </Typography>
                </Box>
              </ListItem>
              <ListItem
                component='li'
                sx={{ display: 'list-item', p: 0, mb: 2 }}
              >
                <Box>
                  <Typography variant='body1'>
                    {t('uploadAccountHistory.dateRangeFormat')} (
                    <Link
                      href={`${import.meta.env.BASE_URL}templates/upload_sample_date_range.xlsx`}
                      underline='hover'
                      download
                    >
                      {t('uploadAccountHistory.downloadTemplate')}
                    </Link>
                    )
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('uploadAccountHistory.dateRangeHint')}
                  </Typography>
                </Box>
              </ListItem>
            </List>

            <Typography
              variant='subtitle1'
              sx={{ mb: 2, fontWeight: 600 }}
            >
              {t('uploadAccountHistory.uploadFile')}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 3, lineHeight: 1.5 }}
            >
              {t('uploadAccountHistory.uploadInstruction')}
            </Typography>

            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              multiple={false}
              accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant='contained'
                onClick={handleSelectFileClick}
              >
                {selectedFile
                  ? t('uploadAccountHistory.replaceFile')
                  : t('uploadAccountHistory.selectFile')}
              </Button>

              {selectedFile && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant='body2'
                    sx={{ mr: 1 }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <IconButton
                    aria-label={t('remove')}
                    onClick={handleRemoveSelectedFile}
                    size='small'
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              )}
            </Box>
            {uploadError && (
              <Alert
                severity='error'
                sx={{ mt: 2 }}
              >
                {t('uploadAccountHistory.uploadErrorNoData')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'flex-end',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {uploadSuccess ? (
          <Button
            variant='outlined'
            onClick={onClose}
            size='large'
          >
            {t('close')}
          </Button>
        ) : (
          <>
            <Button
              variant='outlined'
              onClick={onClose}
              size='large'
            >
              {t('cancel')}
            </Button>
            <Button
              variant='contained'
              size='large'
              disabled={!selectedFile || isUploading}
              onClick={handleUpload}
            >
              {isUploading ? t('uploading') : t('upload')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadAccountHistoryModal;
